import { ImageCard } from "@components/media_card"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { Box, Link, Paper, Stack, Typography } from "@mui/material"
import { AiMessageAnswerProductCard } from "__generated__/graphql"

/**
 * The product carousel the shopper actually received. It leaves the AI as a SEPARATE outgoing
 * message from the answer text, so without it the log shows a reply that references "the first
 * product" with no way to tell which product that was.
 */
export default function AiAnswerProductCards({
  cards,
  emptyLabel,
  title
}: {
  cards: AiMessageAnswerProductCard[]
  /** Shown in place of the photo when the card carried no thumbnail. */
  emptyLabel: string
  title: string
}) {
  if (!cards.length) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: "#0b3d70", fontWeight: 700, display: "block", mb: 0.5 }}
      >
        {`${title} (${cards.length})`}
      </Typography>
      <Stack direction="row" gap={1} sx={{ overflowX: "auto", pb: 0.5 }}>
        {cards.map((card, idx) => (
          <Paper
            key={card.productId ?? `${card.title ?? "card"}-${idx}`}
            elevation={0}
            sx={{
              width: 150,
              minWidth: 150,
              p: 1,
              border: "1px solid #c9e0fb",
              borderRadius: 2,
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              gap: 0.5
            }}
          >
            {card.thumbnailUrl ? (
              <ImageCard
                media={{ path: card.thumbnailUrl, type: "image/jpeg" }}
                size={{ width: 134, height: 134 }}
              />
            ) : (
              <Box
                sx={{
                  width: 134,
                  height: 134,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#f1f1f1",
                  borderRadius: 1
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {emptyLabel}
                </Typography>
              </Box>
            )}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.3,
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2
              }}
            >
              {`${idx + 1}) ${card.title ?? card.productId ?? ""}`}
            </Typography>
            {card.optionsSummary && (
              <Typography sx={{ fontSize: 11 }} color="text.secondary">
                {card.optionsSummary}
              </Typography>
            )}
            {card.price?.amount && (
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                {`${card.price.amount} ${card.price.currency ?? ""}`.trim()}
              </Typography>
            )}
            {card.productUrl && (
              <Link
                href={card.productUrl}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                sx={{
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  wordBreak: "break-all"
                }}
              >
                {card.productUrl}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Link>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  )
}
